<template>
  <div class="c-object-card" :data-type="`Object`" :data-name="title">
    <a :href="url">
      <span class="t-visually-hidden">{{ title }}</span>
    </a>

    <v-gallery
      v-if="finalGalleryImages.length > 0"
      :images="transformedGalleryImages"
      :step-length="1"
      :premise-types="normalizedPremiseTypes"
      :icon-label="iconLabel"
      :title="title"
    />

    <div class="c-object-card__content">
      <!-- Title section -->
      <p class="c-object-card__text">{{ address }}</p>

      <!-- Head with size and city -->
      <p class="c-object-card__byline" v-if="title">{{ title }}</p>

      <!-- Info boxes (size, workplaces, price) -->
      <div
        class="c-object-card__info-boxes"
        v-if="sizeFrom || sizeTo || workplaceDisplay || priceDisplay"
      >
        <!-- Size info box -->
        <div class="c-object-card__info-box" v-if="sizeFrom || sizeTo">
          <i
            aria-hidden="true"
            class="fa-regular fa-ruler c-object-card__info-box-icon"
          ></i>
          <span class="c-object-card__info-box-text">
            {{ fixedSize }}
          </span>
        </div>
 
        <!-- Workplaces info box -->
        <div class="c-object-card__info-box" v-if="workplaceDisplay">
          <i
            aria-hidden="true"
            class="fa-regular fa-users c-object-card__info-box-icon"
          ></i>
          <span class="c-object-card__info-box-text">
            {{ workplaceDisplay }} st
          </span>
        </div>

        <!-- Price info box -->
        <div class="c-object-card__info-box" v-if="priceDisplay">
          <i
            aria-hidden="true"
            class="fa-regular fa-building c-object-card__info-box-icon"
          ></i>
          <span class="c-object-card__info-box-text">
            {{ priceDisplay }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VGallery from "@/components/gallery/VGallery.vue";

export default {
  name: "VObjectCard",
  components: {
    VGallery,
  },
  props: {
    id: {
      type: String,
    },
    url: {
      type: String,
      default: "/",
    },
    // Legacy: single image object
    image: {
      type: [Object, String],
    },
    // New: array of images for gallery
    images: {
      type: [Array, String],
      default: () => [],
    },
    title: {
      type: String,
      default: "Object",
    },
    address: {
      type: String,
    },
    byline: {
      type: String,
      default: "Size",
    },
    sizeFrom: {
      type: Number,
    },
    sizeTo: {
      type: Number,
    },
    // New: richer type with icon
    premiseTypes: {
      type: [Array, String],
      default: () => [],
    },
    features: {
      type: Array,
      default: () => [],
    },
    selectedFeatures: {
      type: Array,
      default: () => [],
    },
    overflowFeaturesCount: {
      type: Number,
    },
    city: {
      type: String,
    },
    district: {
      type: String,
    },
    // Gallery options
    showNavigation: {
      type: Boolean,
      default: true,
    },
    showButtons: {
      type: Boolean,
      default: true,
    },
    showProgress: {
      type: Boolean,
      default: true,
    },
    iconLabel: {
      type: String,
    },
    // Workplace options
    numberOfWorkplaces: {
      type: [String, Number],
    },
    minNumberOfWorkplaces: {
      type: Number,
    },
    maxNumberOfWorkplaces: {
      type: Number,
    },
    // Price option
    pricePerSquare: {
      type: Number,
    },
  },

  computed: {
    /**
     * Parse image prop if it's a JSON string, otherwise return as-is
     */
    parsedImage() {
      if (typeof this.image === "string") {
        try {
          return JSON.parse(this.image);
        } catch (e) {
          return null;
        }
      }
      return this.image;
    },

    /**
     * Parse images prop if it's a JSON string, otherwise return as-is
     */
    parsedImages() {
      if (typeof this.images === "string") {
        try {
          return JSON.parse(this.images);
        } catch (e) {
          return [];
        }
      }
      return this.images;
    },

    /**
     * Parse premiseTypes prop if it's a JSON string, otherwise return as-is
     */
    parsedPremiseTypes() {
      if (typeof this.premiseTypes === "string") {
        try {
          return JSON.parse(this.premiseTypes);
        } catch (e) {
          return [];
        }
      }
      return this.premiseTypes;
    },

    /**
     * Normalize premiseTypes into:
     * [{ type: string, icon: string|null }]
     */
    normalizedPremiseTypes() {
      const source =
        Array.isArray(this.parsedPremiseTypes) &&
        this.parsedPremiseTypes.length > 0
          ? this.parsedPremiseTypes
          : [];

      return source
        .map((p) => {
          if (typeof p === "string") {
            return { type: p, icon: null };
          }

          if (p && typeof p === "object") {
            const resolvedType =
              p.type ||
              p.display ||
              p.value ||
              p.label ||
              p.name ||
              p.title ||
              p.Type ||
              p.Display ||
              p.Value ||
              p.Label ||
              p.Name ||
              p.Title ||
              "";

            const resolvedIcon =
              p.icon ||
              p.iconName ||
              p.iconType ||
              p.Icon ||
              p.IconName ||
              p.IconType ||
              null;

            return {
              type: typeof resolvedType === "string" ? resolvedType.trim() : "",
              icon:
                typeof resolvedIcon === "string" ? resolvedIcon.trim() : null,
            };
          }

          return { type: "", icon: null };
        })
        .filter((p) => p.type);
    },

    fixedSize() {
      const unit = window.location.hostname.endsWith(".dk") ? "m²" : "kvm";
      if (this.sizeFrom === this.sizeTo) {
        return `${this.sizeFrom} ${unit}`;
      } else if (this.sizeFrom && this.sizeTo) {
        return `${this.sizeFrom} - ${this.sizeTo} ${unit}`;
      }
      return "";
    },

    /**
     * Transform gallery images from the incoming format to VGallery format
     * VGallery expects: { id, url, text }
     * Only take the first 4 images for the gallery
     */
    transformedGalleryImages() {
      return this.finalGalleryImages
        .map((img, idx) => ({
          id: `image-${idx}`,
          url: img.src,
          text: img.alt,
        }))
        .slice(0, 4);
    },

    /**
     * Map incoming image shape (imgSrc / src / altText / alt) to gallery format
     * Include responsive image attributes (srcSet, srcSetWebp, sizesAttribute) if available.
     * If no images are provided, use backup placeholder images.
     */
    finalGalleryImages() {
      // If new images array is provided
      if (this.parsedImages && this.parsedImages.length > 0) {
        const mappedImages = this.parsedImages
          .map((img) => ({
            src: (img && (img.imgSrc || img.ImgSrc)) || null,
            alt: (img && (img.altText || img.AltText)) || this.title,
          }))
          .filter((img) => img.src !== null);
        return mappedImages;
      }

      // Legacy single image support
      if (
        this.parsedImage &&
        (this.parsedImage.imgSrc || this.parsedImage.ImgSrc)
      ) {
        return [
          {
            src: this.parsedImage.imgSrc || this.parsedImage.ImgSrc,
            alt:
              this.parsedImage.altText ||
              this.parsedImage.AltText ||
              this.title,
            srcSet: this.parsedImage.srcSet || this.parsedImage.SrcSet || null,
            srcSetWebp:
              this.parsedImage.srcSetWebp ||
              this.parsedImage.SrcSetWebp ||
              null,
            sizesAttribute:
              this.parsedImage.sizesAttribute ||
              this.parsedImage.SizesAttribute ||
              null,
          },
        ];
      }

      // No fallback - return empty array
      return [];
    },

    /**
     * Format workplace count display
     * Priority: numberOfWorkplaces > minNumberOfWorkplaces/maxNumberOfWorkplaces range
     */
    workplaceDisplay() {
      if (this.numberOfWorkplaces) {
        return this.numberOfWorkplaces;
      }

      if (
        this.minNumberOfWorkplaces !== undefined &&
        this.maxNumberOfWorkplaces !== undefined &&
        (this.minNumberOfWorkplaces > 0 || this.maxNumberOfWorkplaces > 0)
      ) {
        if (this.minNumberOfWorkplaces === this.maxNumberOfWorkplaces) {
          return `${this.minNumberOfWorkplaces}`;
        } else {
          return `${this.minNumberOfWorkplaces} - ${this.maxNumberOfWorkplaces}`;
        }
      }

      return "";
    },

    /**
     * Format price display with currency
     */
    priceDisplay() {
      if (this.pricePerSquare) {
        const isDanish = window.location.hostname.endsWith(".dk");
        const locale = isDanish ? "da-DK" : "sv-SE";
        const currency = isDanish ? "DKK" : "kr";
        const unit = isDanish ? "m²" : "kvm";
        return `${this.pricePerSquare.toLocaleString(locale)} ${currency}/${unit}`;
      }
      return "";
    },
  },
};
</script>
